<script setup lang="ts">
import type { ActivityRubricProps } from '../../../../model/assessments/activity-rubric.hook'

import { faTrashCan } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { BtnIcon, BtnSecondary } from '@shared/ui/buttons'

import RubricEditor from './RubricEditor.vue'
import { RUBRIC_REMOVE_HINT, useActivityRubric } from '../../../../model/assessments/activity-rubric.hook'

defineOptions({
  name: 'ActivityRubric',
})

const props = defineProps<ActivityRubricProps>()

const {
  refRubrics,
  refHasRubrics,
  refHasMissing,
  refMissingNote,
  addRubric,
  removeRubric,
} = useActivityRubric({
  screenId: () => props.screenId,
  activityId: () => props.activityId,
})
</script>

<template>
  <div class="activity-rubric">
    <AppText name="body2-medium">
      Rubrics
    </AppText>

    <!-- Reachable only while the question is tutor-reviewed, and that state is defined as having
         at least one rubric — so this shows for a question whose last rubric was just removed,
         on the way back to auto-graded. -->
    <AppText
      v-if="!refHasRubrics"
      name="body2"
      color="slate-500"
    >
      No rubrics yet. Add one for each thing a tutor should judge this question against.
    </AppText>

    <ul class="activity-rubric__items">
      <li
        v-for="rubric in refRubrics"
        :key="rubric.id"
        class="activity-rubric__item"
      >
        <RubricEditor
          v-if="rubric.exists"
          :rubric-id="rubric.id"
        >
          <template #actions>
            <BtnIcon
              :icon="faTrashCan"
              size="small"
              color="red"
              :title="RUBRIC_REMOVE_HINT"
              @click="removeRubric(rubric.id)"
            />
          </template>
        </RubricEditor>

        <!-- Refinement 5's case, surfaced rather than silently repaired: dropping the id here
             would hide the very problem validation exists to report. -->
        <div
          v-else
          class="activity-rubric__missing"
        >
          <AppText
            name="body2"
            color="slate-500"
          >
            Rubric {{ rubric.position }} ({{ rubric.id }}) is referenced but not stored with the
            assessment.
          </AppText>
          <BtnIcon
            :icon="faTrashCan"
            size="small"
            color="red"
            title="Drop this reference"
            @click="removeRubric(rubric.id)"
          />
        </div>
      </li>
    </ul>

    <AppText
      v-if="refHasMissing"
      name="body2"
      color="slate-500"
    >
      {{ refMissingNote }}
    </AppText>

    <BtnSecondary
      text="Add rubric"
      @click="addRubric"
    />
  </div>
</template>

<style scoped lang="scss" src="./ActivityRubric.scss" />
